import React from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from '@mui/material';
import CloudIcon from '@mui/icons-material/Cloud';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';

const CarbonFootprint = () => {
  const emissionData = [
    { category: 'Energy Consumption', value: 8.1, unit: 't CO2e', change: -12, color: '#3b82f6' },
    { category: 'Food Supply Chain', value: 2.4, unit: 't CO2e', change: -5, color: '#10b981' },
    { category: 'Waste Management', value: 1.2, unit: 't CO2e', change: 15, color: '#8b5cf6' },
    { category: 'Transportation', value: 0.8, unit: 't CO2e', change: -8, color: '#f59e0b' },
    { category: 'Water Treatment', value: 0.5, unit: 't CO2e', change: -3, color: '#0ea5e9' },
  ];

  const scopeData = [
    { scope: 'Scope 1', description: 'Direct emissions', value: '2.4 t', percentage: 18 },
    { scope: 'Scope 2', description: 'Indirect energy emissions', value: '8.1 t', percentage: 62 },
    { scope: 'Scope 3', description: 'Other indirect emissions', value: '2.0 t', percentage: 15 },
  ];

  return (
    <Box>
      <Typography variant="h4" gutterBottom fontWeight="bold">
        Carbon Footprint Analysis
      </Typography>
      <Typography variant="body1" color="text.secondary" paragraph>
        Track and analyze your carbon emissions across different scopes and categories.
      </Typography>
      
      {/* Scope Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} md={4}>
          <Card sx={{ height: '100%', borderLeft: '4px solid #ef4444' }}>
            <CardContent>
              <CloudIcon sx={{ fontSize: 40, color: '#ef4444', mb: 2 }} />
              <Typography variant="h5" gutterBottom>
                Scope 1 Emissions
              </Typography>
              <Typography variant="h3" fontWeight="bold" color="#ef4444">
                2.4 t
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Direct emissions from owned sources
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <TrendingDownIcon sx={{ color: '#10b981' }} />
                <Typography variant="body2" color="#10b981" fontWeight="bold">
                  -8.5% from last month
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} md={4}>
          <Card sx={{ height: '100%', borderLeft: '4px solid #3b82f6' }}>
            <CardContent>
              <CloudIcon sx={{ fontSize: 40, color: '#3b82f6', mb: 2 }} />
              <Typography variant="h5" gutterBottom>
                Scope 2 Emissions
              </Typography>
              <Typography variant="h3" fontWeight="bold" color="#3b82f6">
                8.1 t
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Indirect emissions from purchased energy
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <TrendingDownIcon sx={{ color: '#10b981' }} />
                <Typography variant="body2" color="#10b981" fontWeight="bold">
                  -12.2% from last month
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} md={4}>
          <Card sx={{ height: '100%', borderLeft: '4px solid #10b981' }}>
            <CardContent>
              <CloudIcon sx={{ fontSize: 40, color: '#10b981', mb: 2 }} />
              <Typography variant="h5" gutterBottom>
                Scope 3 Emissions
              </Typography>
              <Typography variant="h3" fontWeight="bold" color="#10b981">
                2.0 t
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Other indirect emissions in value chain
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <TrendingDownIcon sx={{ color: '#10b981' }} />
                <Typography variant="body2" color="#10b981" fontWeight="bold">
                  -4.8% from last month
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Total Carbon Footprint */}
      <Card sx={{ mb: 4 }}>
        <CardContent>
          <Typography variant="h5" gutterBottom>
            Total Carbon Footprint
          </Typography>
          <Typography variant="h2" fontWeight="bold" color="primary" gutterBottom>
            12.5 t CO2e
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
            <TrendingDownIcon sx={{ color: '#10b981', fontSize: 32 }} />
            <Box>
              <Typography variant="h6" color="#10b981">
                -8.7% from last month
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Equivalent to 2.8 passenger vehicles driven for one year
              </Typography>
            </Box>
          </Box>
          
          <LinearProgress 
            variant="determinate" 
            value={65} 
            sx={{ 
              height: 12, 
              borderRadius: 6,
              mb: 1,
              backgroundColor: '#e5e7eb',
              '& .MuiLinearProgress-bar': {
                background: 'linear-gradient(90deg, #10b981, #3b82f6)',
                borderRadius: 6,
              }
            }}
          />
          <Typography variant="body2" color="text.secondary">
            65% progress towards carbon neutral target
          </Typography>
        </CardContent>
      </Card>

      {/* Emission Breakdown Table */}
      <Grid container spacing={3}>
        <Grid item xs={12} md={8}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Emission Breakdown by Category
              </Typography>
              <TableContainer component={Paper} variant="outlined">
                <Table>
                  <TableHead>
                    <TableRow sx={{ backgroundColor: 'grey.50' }}>
                      <TableCell><strong>Category</strong></TableCell>
                      <TableCell align="right"><strong>Emissions</strong></TableCell>
                      <TableCell align="right"><strong>Change</strong></TableCell>
                      <TableCell align="right"><strong>% of Total</strong></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {emissionData.map((row, index) => (
                      <TableRow key={index} hover>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <Box sx={{ width: 12, height: 12, borderRadius: '50%', backgroundColor: row.color }} />
                            {row.category}
                          </Box>
                        </TableCell>
                        <TableCell align="right">
                          <Typography fontWeight="bold">
                            {row.value} {row.unit}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.5 }}>
                            {row.change >= 0 ? (
                              <TrendingUpIcon sx={{ color: '#ef4444', fontSize: 16 }} />
                            ) : (
                              <TrendingDownIcon sx={{ color: '#10b981', fontSize: 16 }} />
                            )}
                            <Typography color={row.change >= 0 ? '#ef4444' : '#10b981'}>
                              {row.change >= 0 ? '+' : ''}{row.change}%
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell align="right">
                          <Typography color="text.secondary">
                            {Math.round((row.value / 12.5) * 100)}%
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={4}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Reduction Targets
              </Typography>
              {scopeData.map((item, index) => (
                <Box key={index} sx={{ mb: 3 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="body2" fontWeight="medium">
                      {item.scope}
                    </Typography>
                    <Typography variant="body2" fontWeight="bold">
                      {item.value}
                    </Typography>
                  </Box>
                  <LinearProgress 
                    variant="determinate" 
                    value={item.percentage} 
                    sx={{ 
                      height: 8, 
                      borderRadius: 4,
                      backgroundColor: 'grey.100',
                      '& .MuiLinearProgress-bar': {
                        backgroundColor: index === 0 ? '#ef4444' : 
                                       index === 1 ? '#3b82f6' : '#10b981'
                      }
                    }}
                  />
                  <Typography variant="caption" color="text.secondary">
                    {item.description}
                  </Typography>
                </Box>
              ))}
              <Box sx={{ mt: 3, p: 2, backgroundColor: '#f0fdf4', borderRadius: 2 }}>
                <Typography variant="body2" color="#065f46" fontWeight="medium">
                  🎯 Target: Carbon Neutral by 2025
                </Typography>
                <Typography variant="caption" color="#065f46">
                  You're on track to reduce 45% of emissions by next year
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default CarbonFootprint;